import { jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NO_COLOR } from "../../constants.js";
import { localeText as defaultLocaleText } from "../../localeText.js";
import { colorPickerContext as ctx, defaultState } from "./context.js";
import { useRouter } from "./hooks/useRouter.js";
function ColorPickerContextProvider(props) {
  const {
    value,
    children,
    trigger,
    localeText: userLocaleText,
    onColorChange: userOnChange,
    onAddColor,
    onDeleteColor,
    onInvalidColor,
    swatchSize = defaultState.swatchSize,
    swatchesGap = defaultState.swatchesGap,
    allowRandomColor = defaultState.allowRandomColor,
    allowNoColor = defaultState.allowNoColor,
    customColors = defaultState.customColors,
    swatchesPerRow = defaultState.swatchesPerRow,
    placeholderMode = defaultState.placeholderMode,
    maxRowsBeforeScrolling = defaultState.maxRowsBeforeScrolling
  } = props;
  const router = useRouter(props);
  const setTriggerOpenState = trigger?.onSetOpen;
  const autoCommit = customColors === "none" && router.router.route.id === "palette";
  const [selection, setSelection] = useState(() => /* @__PURE__ */ new Set([value]));
  const [localValue, onChangeLocalValue] = useState(() => value);
  useEffect(() => {
    onChangeLocalValue(value);
    setSelection(/* @__PURE__ */ new Set([value]));
  }, [value]);
  const stableDataRef = useRef({
    autoCommit,
    customColors,
    localValue,
    onDeleteColor,
    setTriggerOpenState,
    userOnChange,
    value
  });
  useEffect(() => {
    stableDataRef.current = {
      autoCommit,
      customColors,
      localValue,
      onDeleteColor,
      setTriggerOpenState,
      userOnChange,
      value
    };
  }, [autoCommit, userOnChange, setTriggerOpenState, customColors, onDeleteColor, value, localValue]);
  const onChange = useCallback((e, ...params) => {
    const [color] = params;
    const allowMultiSelection = e?.metaKey || e?.shiftKey || e?.ctrlKey;
    setSelection((prev) => {
      const performMultiSelection = allowMultiSelection && canMakeMultiSelection(prev, stableDataRef.current.customColors, color, stableDataRef.current.onDeleteColor);
      return new Set(performMultiSelection ? [...Array.from(prev), color] : [color]);
    });
    onChangeLocalValue(color);
    if (stableDataRef.current.autoCommit) {
      stableDataRef.current.setTriggerOpenState?.(false);
      stableDataRef.current.userOnChange(color);
    }
  }, []);
  const onDiscardLocalChanges = useCallback(() => {
    onChangeLocalValue(stableDataRef.current.value);
  }, []);
  const onCommit = useCallback(() => {
    stableDataRef.current.userOnChange(stableDataRef.current.localValue);
    stableDataRef.current.setTriggerOpenState?.(false);
  }, []);
  const onCancel = useCallback(() => {
    if (!stableDataRef.current.setTriggerOpenState) {
      console.warn("cannot call onCancel if no trigger is provided");
      return;
    }
    onChangeLocalValue(stableDataRef.current.value);
    stableDataRef.current.setTriggerOpenState?.(false);
  }, []);
  const ctxValue = useMemo(
    () => ({
      allowNoColor,
      allowRandomColor,
      autoCommit,
      color: value,
      customColors,
      localeText: { ...defaultLocaleText, ...userLocaleText },
      maxRowsBeforeScrolling,
      onAddColor,
      onCancel,
      onChange,
      onChangeSelection: setSelection,
      onCommit,
      onDeleteColor,
      onDiscardLocalChanges,
      onInvalidColor,
      placeholderMode,
      selection,
      swatchesGap,
      swatchesPerRow,
      swatchSize,
      value: localValue,
      ...router
    }),
    [
      onChange,
      onCommit,
      onCancel,
      onInvalidColor,
      onAddColor,
      onDeleteColor,
      allowNoColor,
      customColors,
      maxRowsBeforeScrolling,
      swatchesPerRow,
      localValue,
      userLocaleText,
      placeholderMode,
      selection,
      autoCommit,
      swatchSize,
      onDiscardLocalChanges,
      swatchesGap,
      router,
      value,
      allowRandomColor
    ]
  );
  return /* @__PURE__ */ jsx(ctx.Provider, { value: ctxValue, children });
}
function canMakeMultiSelection(currentSelection, customColors, color, onDeleteColor) {
  if (!onDeleteColor) return false;
  if (color === NO_COLOR) return false;
  if (currentSelection.size === 0) return true;
  if (!customColors || customColors === "none" || customColors === "add-inline") return true;
  const colorsByGroupName = Object.entries(customColors.groups).reduce(
    (acc, [groupId, colorGroup]) => {
      for (const color2 of colorGroup.colors) {
        acc[color2] = groupId;
      }
      return acc;
    },
    {}
  );
  const currentSelectionGroups = Array.from(currentSelection).map((c) => colorsByGroupName[c]);
  const currentSelectionGroupSet = new Set(currentSelectionGroups);
  if (currentSelectionGroupSet.size !== 1) return false;
  const currentColorGroup = currentSelectionGroups[0];
  if (currentColorGroup === void 0) return false;
  return colorsByGroupName[color] === currentColorGroup;
}
export {
  ColorPickerContextProvider
};
//# sourceMappingURL=ColorPickerContextProvider.js.map
